import { useEffect, useState } from 'react';

import { api } from '@/lib/api';

/**
 * The day this workspace started recording, for every date control at once.
 *
 * A workspace has exactly one of these and it changes about never, so this is
 * fetched once per session and shared through a module-level cache rather than
 * per component. The date range control is rendered on a dozen pages; making
 * each one fetch, or making each page thread the value down as a prop, would
 * be a worse version of the same answer.
 *
 * Returns null until it is known, and null forever if the request fails - both
 * mean "no clamp", which is exactly how the control behaved before this.
 */

let cached = null;
let inflight = null;
const waiters = new Set();

function load() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    // '/api/commerce/data-start', like every other call site. Without the
    // prefix this hit nginx's catch-all, followed the 301 to /app/, and
    // resolved with the SPA's own index.html - r.data.date undefined, cached
    // {date:null}, and the data-start clamp silently dead in the dashboard
    // since the day this hook was written. The tolerant "?: {date:null}" is
    // what kept it invisible: HTML parsed as "no date" instead of erroring.
    inflight = api.get('/api/commerce/data-start')
      .then((r) => {
        cached = r.data && r.data.date ? r.data : { date: null };
        waiters.forEach((fn) => fn(cached));
        return cached;
      })
      .catch((e) => {
        // Say so, and do NOT cache the failure. Caching {date:null} made one
        // bad request disable the data-start clamp for the whole session with
        // nothing in the console - the calendar quietly offered dates the
        // server would refuse, and nobody could tell why.
        console.warn('[data-start] fetch failed - clamp disabled until it succeeds:', e?.message || e);
        waiters.forEach((fn) => fn(null));
        return null;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Clears the cache when the user switches workspace - the date moves with it. */
export function resetDataStart() { cached = null; inflight = null; }
// For the boot prefetch (lib/route-preload): a report's date control needs this
// before it can label its presets, so a fresh load asks alongside the report.
export const preloadDataStart = () => { load().catch(() => {}); };

export function useDataStart() {
  const [value, setValue] = useState(cached);

  useEffect(() => {
    if (cached) { setValue(cached); return undefined; }
    let alive = true;
    const fn = (v) => { if (alive) setValue(v); };
    waiters.add(fn);
    load();
    return () => { alive = false; waiters.delete(fn); };
  }, []);

  return value && value.date ? value : null;
}

export default useDataStart;
